import { offline } from '@/lib/offline.js'

/**
 * Sales materials on the device (D-332): the same list and the same fits as Core gives online
 * (SalesMaterialController), from what the device keeps — the materials, their files kept, and
 * the records they are fitted to.
 */

/** Why a material fits a record: about what it has, its competitors, its industry, its stage. */
export function fitsOf(m, ctx) {
  const about = (m.about || []).map((e) => `${e.type}:${e.uuid}`)
  const out = []
  if (about.some((a) => ctx.about.includes(a))) out.push('about')
  if ((m.competitors || []).some((c) => ctx.competitors.includes(c))) out.push('competitor')
  if (ctx.industry && (m.industries || []).includes(ctx.industry)) out.push('industry')
  if (ctx.stage && (m.stages || []).includes(ctx.stage)) out.push('stage')
  return out
}

const held = async (entity, uuid) => {
  if (!uuid) return null
  const row = await offline.local(entity).get(uuid)
  return row && !row._outOfScopeAt ? row : null
}

/** What a record kept on the device is about (`for`: "offer:uuid", "opportunity:uuid", "account:uuid"). */
export async function localContext(forWhat) {
  const [type, uuid] = String(forWhat || '').split(':')
  const ctx = { about: [], competitors: [], industry: null, stage: null }
  let opportunity = null
  let account = null
  if (type === 'offer') {
    const offer = await held('offer', uuid)
    for (const m of offer?.modules || []) ctx.about.push(`module:${m.uuid}`, ...(m.product_uuid ? [`product:${m.product_uuid}`] : []))
    opportunity = await held('opportunity', offer?.opportunity_uuid || offer?.opportunity?.uuid)
    account = await held('account', offer?.account_uuid || offer?.account?.uuid)
  } else if (type === 'opportunity') {
    opportunity = await held('opportunity', uuid)
    for (const i of opportunity?.product_interests || []) {
      if (i.product?.uuid) ctx.about.push(`product:${i.product.uuid}`)
      if (i.module?.uuid) ctx.about.push(`module:${i.module.uuid}`, ...(i.module.product_uuid ? [`product:${i.module.product_uuid}`] : []))
    }
  } else if (type === 'account') {
    account = await held('account', uuid)
  }
  if (opportunity) {
    ctx.competitors = (opportunity.competitors || []).map((c) => c.uuid)
    ctx.stage = opportunity.stage?.uuid || opportunity.stage_uuid || null
    account ||= await held('account', opportunity.account_uuid || opportunity.account?.uuid)
  }
  ctx.industry = account?.industry || null
  return ctx
}

/**
 * The materials kept on the device, as `/materials` lists them: active ones, filtered by category,
 * words, product (its modules too), competitor, industry; those fitting the record first. Their
 * files are those the device keeps (`cached`).
 */
export async function localMaterials({ category = '', q = '', product = '', competitor = '', industry = '', for: forWhat = '' } = {}, meta) {
  const files = (await offline.db.table('files').toArray()).filter((f) => String(f.attachable || '').startsWith('sales_material:'))
  const filesOf = new Map()
  for (const f of files) {
    const key = f.attachable.slice('sales_material:'.length)
    filesOf.set(key, [...(filesOf.get(key) || []), { ...f, cached: true }])
  }
  const ctx = await localContext(forWhat)
  const words = q.toLowerCase().split(/\s+/).filter(Boolean)
  const today = new Date().toISOString().slice(0, 10)
  const rows = (await offline.local('sales_material').toArray())
    .filter((m) => !m._outOfScopeAt && m.is_active !== false)
    .filter((m) => !category || m.category === category)
    .filter((m) => !competitor || (m.competitors || []).includes(competitor))
    .filter((m) => !industry || (m.industries || []).includes(industry))
    .filter((m) => !product || (m.about_items || []).some((e) => (e.type === 'product' && e.uuid === product) || (e.type === 'module' && e.product_uuid === product)))
    .filter((m) => words.every((w) => `${m.name || ''} ${m.description || ''}`.toLowerCase().includes(w)))
    .map((m) => ({
      uuid: m.uuid, name: m.name, description: m.description, is_internal: !!m.is_internal, review_date: m.review_date || null,
      to_review: !!m.review_date && m.review_date < today,
      category: m.category ? { key: m.category, label: meta.dictionary('material_category').entries.find((e) => e.key === m.category)?.label || { pl: m.category } } : null,
      files: (filesOf.get(m.uuid) || []).sort((a, b) => String(a.created_at).localeCompare(String(b.created_at))),
      links: m.links || [], about_items: m.about_items || [], matched: fitsOf(m, ctx),
    }))
  rows.sort((a, b) => b.matched.length - a.matched.length || String(a.name).localeCompare(String(b.name)))
  return rows
}

/** The uuids of the files the device keeps — to say, online too, which a material has here. */
export async function keptFiles() {
  return new Set((await offline.db.table('files').toArray()).map((f) => f.uuid))
}
